<template>
  <!-- The bar is two combo fields and a switch. Each field is the place you
       type AND the thing that opens its list, so adding one IP costs no clicks
       at all — the box is already there. The list below it holds only the
       entries, because the add-field is no longer inside it; that is what keeps
       it short.

       The open list is part of the layout, not a popover floating over it. A
       floating panel is wider than the control that anchors it, so it covered
       the field beside it and the Save button underneath. In flow it pushes
       what follows down, so nothing can ever be hidden behind it. -->
  <div ref="root" class="ipf admin-section" :class="{ 'ipf--off': !exclude }">
    <div class="ipf__bar">
      <span class="ipf__label">
        <i class="fa-solid fa-filter-circle-xmark ipf__labelIcon"></i>
        <span>Excluded traffic</span>
      </span>

      <!-- IP addresses -->
      <div
        class="ipf__combo"
        :class="{ 'is-open': openPanel === 'ip', 'is-focus': ipFocus, 'is-empty': !ips.length }"
        :title="ips.length ? `Excluding ${ips.join(', ')}` : 'No IP address is excluded yet'"
        @click="openIp"
      >
        <i class="fa-solid fa-network-wired ipf__comboIcon"></i>
        <input
          ref="ipInput"
          v-model="ipDraft"
          class="ipf__field"
          placeholder="Type an IP and press Enter"
          aria-label="Add an IP address to exclude"
          autocomplete="off"
          role="combobox"
          :aria-expanded="openPanel === 'ip'"
          @focus="onIpFocus"
          @blur="onIpBlur"
          @keydown.enter.prevent="commitIp"
          @keydown="onIpKey"
          @paste="onIpPaste"
        />
        <span v-if="ips.length" class="ipf__count">{{ ips.length }}</span>
        <button
          type="button"
          class="ipf__caretBtn"
          :aria-label="openPanel === 'ip' ? 'Hide the excluded IPs' : 'Show the excluded IPs'"
          @click.stop="togglePanel('ip')"
        ><i class="fa-solid fa-chevron-down ipf__caret"></i></button>
      </div>

      <!-- People -->
      <div
        class="ipf__combo"
        :class="{ 'is-open': openPanel === 'user', 'is-focus': userFocus, 'is-empty': !userNames.length }"
        :title="userNames.length ? `Excluding ${userNames.join(', ')}` : 'No person is excluded yet'"
        @click="openUser"
      >
        <i class="fa-solid fa-user-group ipf__comboIcon"></i>
        <input
          ref="userInput"
          v-model="userDraft"
          class="ipf__field"
          placeholder="Type a name and press Enter"
          aria-label="Add a person to exclude"
          autocomplete="off"
          role="combobox"
          :aria-expanded="openPanel === 'user'"
          @focus="onUserFocus"
          @blur="onUserBlur"
          @input="onUserType"
          @keydown.enter.prevent="onUserEnter"
          @keydown.down.prevent="moveHighlight(1)"
          @keydown.up.prevent="moveHighlight(-1)"
          @keydown.esc.stop="closeSuggest"
        />
        <span
          v-show="unmatchedNames.length"
          class="ipf__comboWarn"
          :title="`${unmatchedList} match no account`"
        ><i class="fa-solid fa-triangle-exclamation"></i></span>
        <span v-if="userNames.length" class="ipf__count">{{ userNames.length }}</span>
        <button
          type="button"
          class="ipf__caretBtn"
          :aria-label="openPanel === 'user' ? 'Hide the excluded people' : 'Show the excluded people'"
          @click.stop="togglePanel('user')"
        ><i class="fa-solid fa-chevron-down ipf__caret"></i></button>
      </div>

      <span class="ipf__sep" aria-hidden="true"></span>

      <label class="ipf__switch" :title="exclude
        ? 'Turn off to see everything, including known traffic'
        : 'Turn on to strip known traffic out of this report'">
        <input type="checkbox" v-model="exclude" @change="emitApply" />
        <span class="ipf__track" aria-hidden="true"><span class="ipf__thumb"></span></span>
        <span class="ipf__switchText">{{ exclude ? 'On' : 'Off' }}</span>
      </label>

      <span class="ipf__spacer"></span>

      <!-- There is deliberately no "unsaved" marker. The button is the state:
           live when there is something to save, spent when there is not. Two
           indicators for one fact only ever disagree, and this one did. -->
      <transition name="ipf-fade">
        <span v-if="savedMsg" class="ipf__saved"><i class="fa-solid fa-check"></i> {{ savedMsg }}</span>
      </transition>
      <button
        class="admin-btn admin-btn--primary ipf__saveBtn"
        :disabled="!canSave"
        :title="canSave ? 'Save the exclusion list' : 'Nothing to save'"
        @click="save"
      >
        <!-- v-show, not v-if/v-else. FontAwesome's js/all.js (imported in
             main.ts) swaps every <i class="fa-*"> for an <svg> behind Vue's
             back, so the <i> Vue thinks it owns here is a node that is no
             longer in the document. Mounting the spinner in its place made
             Vue insert into a null parent, and that TypeError aborted the
             WHOLE component patch half-way: the button never went disabled,
             the caret never turned back, and the open list never closed —
             all three read as "save did nothing". Both icons are mounted
             once and only their wrapper's display is toggled, so nothing is
             ever mounted or unmounted after the first render. -->
        <span class="ipf__btnIcon" v-show="saving"><span class="admin-spinner ipf__miniSpinner"></span></span>
        <span class="ipf__btnIcon" v-show="!saving"><i class="fa-solid fa-check"></i></span>
        {{ saving ? 'Saving' : 'Save' }}
      </button>
    </div>

    <!-- Kept on its own line directly under the bar, next to the field it is
         about and the button it explains, rather than under the open list
         where a shut list would hide it. -->
    <p v-if="ipError" class="ipf__msg ipf__msg--bad ipf__barMsg">
      <i class="fa-solid fa-circle-exclamation"></i> {{ ipError }}
    </p>
    <p v-else-if="error" class="ipf__msg ipf__msg--bad ipf__barMsg">
      <i class="fa-solid fa-circle-exclamation"></i> {{ error }}
    </p>

    <!-- ---- IP list: entries only ---- -->
    <div v-if="openPanel === 'ip'" class="ipf__panel">
      <ul v-if="ips.length" class="ipf__list">
        <li v-for="ip in ips" :key="ip" class="ipf__row">
          <i class="fa-solid fa-network-wired ipf__rowIcon"></i>
          <span class="ipf__rowText">{{ ip }}</span>
          <button
            type="button"
            class="ipf__rowX"
            :aria-label="`Stop excluding ${ip}`"
            title="Remove"
            @click="removeIp(ip)"
          ><i class="fa-solid fa-xmark"></i></button>
        </li>
      </ul>
      <p v-else class="ipf__empty">Nothing excluded by IP yet.</p>
    </div>

    <!-- ---- People list ---- -->
    <div v-if="openPanel === 'user'" class="ipf__panel">
      <!-- Real accounts, so an admin picks a person instead of guessing a
           spelling that silently matches nobody. -->
      <ul v-if="showSuggest" class="ipf__suggest" role="listbox">
        <li v-if="suggestLoading" class="ipf__suggestEmpty">
          <span class="admin-spinner ipf__miniSpinner"></span> Searching&hellip;
        </li>
        <li v-else-if="!suggestions.length" class="ipf__suggestEmpty">
          No account matches "{{ userDraft.trim() }}" &mdash; press Enter to exclude the name anyway
        </li>
        <li
          v-for="(u, i) in suggestions"
          :key="u.userID"
          class="ipf__suggestItem"
          :class="{ 'is-active': i === highlight }"
          role="option"
          :aria-selected="i === highlight"
          @mousedown.prevent="pickUser(u)"
          @mouseenter="highlight = i"
        >
          <span class="ipf__suggestName">{{ u.userName || u.fullName || u.email }}</span>
          <span class="ipf__suggestMeta">{{ u.email }}</span>
        </li>
      </ul>

      <ul v-if="userNames.length" class="ipf__list">
        <li
          v-for="name in userNames"
          :key="name"
          class="ipf__row"
          :class="{ 'ipf__row--warn': isUnmatched(name) }"
          :title="isUnmatched(name)
            ? `No account matches ${name}, so this excludes nothing`
            : `Excluding ${name}`"
        >
          <i class="fa-solid ipf__rowIcon" :class="isUnmatched(name)
            ? 'fa-triangle-exclamation'
            : 'fa-circle-user'"></i>
          <span class="ipf__rowText">{{ name }}</span>
          <button
            type="button"
            class="ipf__rowX"
            :aria-label="`Stop excluding ${name}`"
            title="Remove"
            @click="removeUser(name)"
          ><i class="fa-solid fa-xmark"></i></button>
        </li>
      </ul>
      <p v-else-if="!showSuggest" class="ipf__empty">Nobody is excluded by name yet.</p>

      <p v-if="unmatchedNames.length" class="ipf__msg ipf__msg--warn">
        <i class="fa-solid fa-triangle-exclamation"></i>
        {{ unmatchedList }} {{ unmatchedNames.length === 1 ? 'matches' : 'match' }}
        no account, so {{ unmatchedNames.length === 1 ? 'it excludes' : 'they exclude' }} nothing
      </p>
    </div>

  </div>
</template>

<script>
import axios from "axios";

// Shared exclusion list for the admin reports. One global list for all admins,
// loaded from / saved to /api/excluded-ips, with two OR-combined dimensions:
// IPs and usernames. A row is hidden if EITHER matches it.
//
// Admins never type raw user ids. The backend resolves each excluded username
// to its userID internally, because the Viewers Info report records the viewer
// as viewerUserID and no name at all. Any saved name it could not resolve
// comes back in `unmatchedNames` and is flagged on its own row.
//
// After a save - or when the exclude switch is toggled - it emits `apply` with
// { includeKnownIps } so the parent page can re-fetch its report. That contract
// is unchanged; five report surfaces depend on it.
const IPV4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
const IPV6ISH = /^[0-9a-f:]+$/i;

export default {
  name: "IpFilterBox",
  emits: ["apply"],
  data() {
    return {
      ips: [],
      userNames: [],
      // The lists as the server last confirmed them, for the Save button.
      savedIps: [],
      savedUserNames: [],
      // The drafts as of the last save. A typed IP that will not parse stays
      // in its box on purpose — discarding what someone typed is worse than
      // keeping it — but it must not leave Save lit for ever afterwards. Save
      // goes live again only when a draft becomes something NEW to enter.
      savedIpDraft: "",
      savedUserDraft: "",
      unmatchedNames: [],

      ipDraft: "",
      userDraft: "",
      ipFocus: false,
      userFocus: false,
      ipError: "",

      suggestions: [],
      suggestLoading: false,
      showSuggest: false,
      highlight: -1,
      suggestTimer: null,

      // 'ip' | 'user' | null — only one list is ever open.
      openPanel: null,

      exclude: true,
      saving: false,
      error: null,
      savedMsg: "",
    };
  },
  computed: {
    listsDirty() {
      return (
        this.ips.join(" ") !== this.savedIps.join(" ") ||
        this.userNames.join(" ") !== this.savedUserNames.join(" ")
      );
    },
    // Text typed but not yet entered is unsaved too, so Save must be reachable
    // for it. Gating the button on the committed lists alone is what once made
    // Save impossible to press: it was still disabled at the moment it was
    // clicked, and a disabled button fires no click at all.
    canSave() {
      if (this.saving) return false;
      if (this.listsDirty) return true;
      // Text typed but not yet entered is unsaved too, so Save must be
      // reachable for it — gating on the committed lists alone is what once
      // made Save impossible to press. But once a save has been through this
      // draft, it is no longer news: leaving the button lit then reads as a
      // save that did not take.
      const ip = this.ipDraft.trim();
      const user = this.userDraft.trim();
      if (ip && ip !== this.savedIpDraft) return true;
      if (user && user !== this.savedUserDraft) return true;
      return false;
    },
    unmatchedList() {
      return this.unmatchedNames.map((n) => `"${n}"`).join(", ");
    },
    ipLabel() {
      if (!this.ips.length) return "No IPs";
      return `${this.ips.length} IP${this.ips.length === 1 ? "" : "s"}`;
    },
    userLabel() {
      if (!this.userNames.length) return "No people";
      return `${this.userNames.length} ${this.userNames.length === 1 ? "person" : "people"}`;
    },
  },
  watch: {
    // Emptying the box clears its complaint; otherwise the error outlives the
    // text it was about and keeps accusing a field that is now blank.
    ipDraft(v) {
      if (!String(v).trim()) this.ipError = "";
    },
  },
  mounted() {
    this.load();
    // Capture phase, deliberately. Sibling controls on these pages stop
    // mousedown in the bubble phase — DatePicker does, to protect its own
    // outside-click handler — and a bubble listener on document never hears
    // those clicks, so this list stayed open depending on WHERE you clicked.
    // Capture runs document-downward, before any stopPropagation can fire.
    document.addEventListener("click", this.onDocDown, true);
    document.addEventListener("focusin", this.onDocFocus, true);
    document.addEventListener("keydown", this.onDocKey, true);
  },
  beforeUnmount() {
    clearTimeout(this.suggestTimer);
    // The capture flag is part of the listener's identity — remove without it
    // and the listener stays attached for the life of the page.
    document.removeEventListener("click", this.onDocDown, true);
    document.removeEventListener("focusin", this.onDocFocus, true);
    document.removeEventListener("keydown", this.onDocKey, true);
  },
  methods: {
    authHeaders() {
      const token = localStorage.getItem("auth-token");
      return { headers: { Authorization: `Bearer ${token}` } };
    },

    // The caret toggles. Clicking or focusing the field only ever OPENS: the
    // field is also where you type, so a second click inside it while typing
    // must not shut the list you are reading.
    togglePanel(which) {
      this.openPanel = this.openPanel === which ? null : which;
      this.closeSuggest();
      if (!this.openPanel) return;
      this.$nextTick(() => {
        const input = which === "ip" ? this.$refs.ipInput : this.$refs.userInput;
        input && input.focus();
      });
    },
    // Clicking anywhere off the control puts the list away — the ordinary way
    // out of an open menu, and the one an admin reaches for before hunting for
    // the caret again.
    //
    // mousedown, not click: it fires before the field blurs, and the field
    // lives on the bar rather than inside the list, so it is still mounted to
    // receive that blur and commit a finished IP. Closing on click would race
    // with focus moving away. Registered in the capture phase — see mounted().
    // True when the event started anywhere inside this control.
    //
    // composedPath() rather than root.contains(e.target): it is the ancestor
    // chain as it was when the event was dispatched, so it still answers
    // correctly if the clicked node has since been detached by a re-render.
    insideMe(e) {
      const root = this.$refs.root || this.$el;
      if (!root || root.nodeType !== 1) return false;
      const path = typeof e.composedPath === "function" ? e.composedPath() : null;
      return path && path.length ? path.indexOf(root) !== -1 : root.contains(e.target);
    },
    // click, not mousedown. The open list is IN the layout, so closing it
    // collapses its height and everything below jumps up by that much. Close on
    // mousedown and that reflow happens BEFORE the click half of the same
    // gesture is delivered, so the click lands on whatever slid under the
    // cursor — the combo field, which reopens the list on the spot. Closing on
    // click puts the reflow after the gesture is over, so there is nothing left
    // to misfire. The field has already blurred by then (blur runs at
    // mousedown), so a finished IP is still committed.
    onDocDown(e) {
      if (!this.openPanel) return;
      if (this.insideMe(e)) return;
      this.closePanel();
    },
    // Tabbing or focusing away is leaving too.
    onDocFocus(e) {
      if (!this.openPanel) return;
      if (!this.insideMe(e)) this.closePanel();
    },
    // Escape backs out one layer at a time: the account suggestions first,
    // then the list itself.
    onDocKey(e) {
      if (e.key !== "Escape" || !this.openPanel) return;
      if (this.showSuggest) { this.closeSuggest(); return; }
      this.closePanel();
    },
    closePanel() {
      this.openPanel = null;
      this.closeSuggest();
    },
    openIp() {
      if (this.openPanel !== "ip") { this.openPanel = "ip"; this.closeSuggest(); }
      this.focusIp();
    },
    openUser() {
      if (this.openPanel !== "user") this.openPanel = "user";
      this.focusUser();
    },
    onIpFocus() {
      this.ipFocus = true;
      if (this.openPanel !== "ip") { this.openPanel = "ip"; this.closeSuggest(); }
    },
    onUserFocus() {
      this.userFocus = true;
      if (this.openPanel !== "user") this.openPanel = "user";
      this.onUserType();
    },

    /* ---- list state ---- */
    applyData(data) {
      this.ips = (data.ips || []).slice();
      this.userNames = (data.userNames || []).slice();
      this.savedIps = this.ips.slice();
      this.savedUserNames = this.userNames.slice();
      this.savedIpDraft = this.ipDraft.trim();
      this.savedUserDraft = this.userDraft.trim();
      this.unmatchedNames = data.unmatchedNames || [];
    },
    isUnmatched(name) {
      const key = String(name).toLowerCase();
      return this.unmatchedNames.some((n) => String(n).toLowerCase() === key);
    },
    has(list, value) {
      const key = String(value).toLowerCase();
      return list.some((v) => String(v).toLowerCase() === key);
    },

    /* ---- IPs ---- */
    focusIp() {
      this.$refs.ipInput && this.$refs.ipInput.focus();
    },
    validIp(value) {
      const m = IPV4.exec(value);
      if (m) return m.slice(1).every((o) => Number(o) <= 255);
      // Loose on v6 - anything hex-and-colons is closer to right than a guess.
      return value.includes(":") && IPV6ISH.test(value);
    },
    addIp(value) {
      const ip = String(value).trim();
      if (!ip) return true;
      if (!this.validIp(ip)) {
        this.ipError = `"${ip}" does not look like an IP address`;
        return false;
      }
      this.ipError = "";
      if (!this.has(this.ips, ip)) this.ips.push(ip);
      return true;
    },
    commitIp() {
      if (!this.ipDraft.trim()) { this.ipError = ""; return; }
      if (this.addIp(this.ipDraft)) this.ipDraft = "";
    },
    removeIp(ip) {
      this.ips = this.ips.filter((v) => v !== ip);
      this.ipError = "";
    },
    // Comma and space end a token the way Enter does.
    //
    // Backspace does NOT delete the previous entry. A token field conventionally
    // does that, and here it was a trap: the box takes focus the moment its list
    // opens, so a backspace meant for a typo silently deleted an entry that was
    // already saved. The list then read as unsaved again immediately after a
    // save, and one save went out having quietly dropped a name this way.
    // Removing an entry is the x on its row, which is visible and aimed.
    onIpKey(e) {
      if (e.key === "," || e.key === " ") {
        e.preventDefault();
        this.commitIp();
      }
    },
    // Blur takes an address that parses; anything else stays in the box for the
    // admin to finish rather than becoming a bogus entry or a red error on a
    // stray click.
    onIpBlur() {
      this.ipFocus = false;
      const v = this.ipDraft.trim();
      if (v && this.validIp(v)) this.commitIp();
    },
    onIpPaste(e) {
      // Pasting a list from a spreadsheet should become entries.
      const text = (e.clipboardData || window.clipboardData).getData("text") || "";
      if (!/[\s,;]/.test(text)) return;
      e.preventDefault();
      let ok = true;
      for (const part of text.split(/[\s,;]+/)) {
        if (part.trim() && !this.addIp(part)) ok = false;
      }
      if (ok) this.ipDraft = "";
    },

    /* ---- people + account picker ---- */
    focusUser() {
      this.$refs.userInput && this.$refs.userInput.focus();
    },
    addUser(name) {
      const value = String(name).trim();
      if (!value) return;
      if (!this.has(this.userNames, value)) this.userNames.push(value);
      this.userDraft = "";
      this.closeSuggest();
    },
    removeUser(name) {
      this.userNames = this.userNames.filter((v) => v !== name);
      this.unmatchedNames = this.unmatchedNames.filter((v) => v !== name);
    },
    onUserEnter() {
      if (this.showSuggest && this.highlight >= 0 && this.suggestions[this.highlight]) {
        this.pickUser(this.suggestions[this.highlight]);
      } else {
        // No pick: exclude exactly what was typed. The backend will report it
        // as unmatched if no account has that name.
        this.addUser(this.userDraft);
      }
    },
    onUserBlur() {
      this.userFocus = false;
      // Let a mousedown on a suggestion land before the list unmounts. The
      // draft itself is left alone — it is a search query as much as an entry,
      // and Save commits it anyway.
      setTimeout(() => { this.showSuggest = false; }, 150);
    },
    moveHighlight(step) {
      if (!this.showSuggest || !this.suggestions.length) return;
      const n = this.suggestions.length;
      this.highlight = (this.highlight + step + n) % n;
    },
    closeSuggest() {
      this.showSuggest = false;
      this.suggestions = [];
      this.highlight = -1;
    },
    onUserType() {
      clearTimeout(this.suggestTimer);
      const q = this.userDraft.trim();
      if (q.length < 2) { this.closeSuggest(); return; }
      this.showSuggest = true;
      this.highlight = -1;
      this.suggestTimer = setTimeout(() => this.fetchSuggestions(q), 220);
    },
    async fetchSuggestions(q) {
      this.suggestLoading = true;
      try {
        const { data } = await axios.post(
          "/api/userstats/user-suggest",
          { q },
          this.authHeaders()
        );
        // Ignore a response that lost the race with newer typing.
        if (q !== this.userDraft.trim()) return;
        this.suggestions = (data.users || []).filter(
          (u) => !this.has(this.userNames, u.userName || u.email || "")
        );
      } catch {
        this.suggestions = [];
      } finally {
        this.suggestLoading = false;
      }
    },
    pickUser(u) {
      this.addUser(u.userName || u.email || u.fullName || u.userID);
    },

    /* ---- load / save ---- */
    async load() {
      try {
        const { data } = await axios.get("/api/excluded-ips", this.authHeaders());
        this.applyData(data);
      } catch (err) {
        this.error = err.response?.data?.message || err.message;
      }
    },
    async save() {
      // The disabled attribute is the visible half of the double-click guard,
      // and it only works if the patch that sets it actually reaches the DOM.
      // Guard the handler itself too, so a second click can never start a
      // second POST even when rendering is having a bad day.
      if (this.saving) return;
      // Whatever is typed but not yet entered counts as intended input: Save is
      // the one button pressed expecting everything on screen to be kept.
      //
      // A draft that will not parse must NOT block the save. This used to
      // return early on a bad IP, so any leftover text in that box silently
      // killed every later save, including one meant for the other list. What
      // is already in the lists goes; the bad draft stays in its box with the
      // error printed beside the Save button, where it cannot be missed.
      const ip = this.ipDraft.trim();
      const rejectedIp = ip && !this.addIp(ip) ? ip : "";
      if (ip && !rejectedIp) this.ipDraft = "";
      if (this.userDraft.trim()) this.addUser(this.userDraft);

      this.saving = true;
      this.error = null;
      this.savedMsg = "";
      try {
        const { data } = await axios.post(
          "/api/excluded-ips",
          { ips: this.ips, userNames: this.userNames },
          this.authHeaders()
        );
        this.applyData(data);
        // The job is done, so the list goes away and the button goes quiet.
        // Leaving it open after a save made a finished save look unfinished.
        this.openPanel = null;
        this.closeSuggest();
        this.savedMsg = "Saved";
        // A draft that would not parse is left in its box, which keeps Save
        // live — correctly, there IS something unentered. Without saying so,
        // that reads as a save that did not take. Name it.
        if (rejectedIp) {
          this.ipError =
            `"${rejectedIp}" does not look like an IP address. Everything else was saved — ` +
            `clear or correct that box to finish.`;
        }
        this.emitApply();
        setTimeout(() => { this.savedMsg = ""; }, 2500);
      } catch (err) {
        this.error = err.response?.data?.message || err.message;
      } finally {
        this.saving = false;
      }
    },
    emitApply() {
      this.$emit("apply", { includeKnownIps: !this.exclude });
    },
  },
};
</script>

<style scoped>
.ipf {
  padding: 10px 12px;
  border: 1px solid var(--g-border, #e9ebf2);
  border-radius: var(--g-radius, 12px);
  background: var(--g-surface, #fff);
  box-shadow: var(--g-shadow-2, 0 1px 2px rgba(17,24,39,.05));
}

/* ---- the bar ---- */
.ipf__bar {
  display: flex;
  align-items: center;
  gap: var(--g-sp-2, 8px);
  flex-wrap: wrap;
  /* Row-gap matters once this wraps on a narrow window: without it the wrapped
     Save sits flush against the fields above it. */
  row-gap: 9px;
}
/* Pushes the save cluster to the right end of the row. Everything before it is
   the editing controls; the button that commits them is the last thing on the
   line, which is where a form's primary action is looked for. */
.ipf__saveBtn {
  flex: 0 0 auto;
  height: 44px;
  padding: 0 20px;
  font-size: 14px;
}
.ipf__label {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex: 0 0 auto;
  height: 36px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--g-text, #111827);
  white-space: nowrap;
}
.ipf__labelIcon { font-size: 13px; color: var(--g-blue, #4f46e5); }
.ipf__sep { width: 1px; height: 20px; background: var(--g-border, #e9ebf2); flex: 0 0 auto; }
.ipf__spacer { flex: 1 1 auto; }

/* Off is a state worth seeing from across the room: the report below is
   unfiltered, which is the one thing that makes these numbers different. */
.ipf--off { border-color: var(--g-border-strong, #dcdfe9); background: var(--g-surface-2, #fafbfd); }
.ipf--off .ipf__labelIcon { color: var(--g-text-disabled, #9aa2b1); }

/* ---- the two combo fields ----
   Bigger than the counts they replace, because they are typed into now: a
   count only had to be read, a field has to be aimed at and read back. 44px
   tall, and wide enough for an IPv4 address plus its count badge. */
.ipf__combo {
  display: flex;
  align-items: center;
  gap: 9px;
  flex: 0 0 auto;
  width: 286px;
  height: 44px;
  padding: 0 7px 0 13px;
  border: 1px solid var(--g-border-strong, #dcdfe9);
  border-radius: var(--g-radius-sm, 9px);
  background: var(--g-surface, #fff);
  cursor: text;
  transition: border-color .16s ease, box-shadow .16s ease, background-color .16s ease;
}
.ipf__combo:hover { border-color: var(--g-border-hover, #c9cede); }
.ipf__combo.is-focus,
.ipf__combo.is-open {
  border-color: var(--g-blue, #4f46e5);
  box-shadow: var(--g-focus-ring, 0 0 0 3px rgba(79,70,229,.16));
}
.ipf__comboIcon { flex: 0 0 auto; font-size: 14px; color: var(--g-blue, #4f46e5); }
/* An empty list is a normal state, not a problem — it just recedes. */
.ipf__combo.is-empty .ipf__comboIcon { color: var(--g-text-disabled, #9aa2b1); }
.ipf__comboWarn { flex: 0 0 auto; font-size: 13px; color: var(--g-amber, #b54708); }
/* Wrapper that keeps a FontAwesome-rewritten icon out of Vue's way — Vue owns
   the span, FontAwesome owns whatever ends up inside it. */
.ipf__btnIcon { display: inline-flex; align-items: center; justify-content: center; }

.ipf__field {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  font-size: 14.5px;
  font-weight: 500;
  color: var(--g-text, #111827);
  padding: 0;
}
.ipf__field::placeholder { color: var(--g-text-disabled, #9aa2b1); font-weight: 500; }

/* The count as a number, because the placeholder that would say it disappears
   the moment anything is typed. */
.ipf__count {
  flex: 0 0 auto;
  min-width: 24px;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--g-blue-tint, #eef2ff);
  color: var(--g-blue, #4f46e5);
  font-size: 12.5px;
  font-weight: 700;
  text-align: center;
}
.ipf__caretBtn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: none;
  border-radius: 7px;
  background: none;
  cursor: pointer;
}
.ipf__caretBtn:hover { background: var(--g-hover, #f2f4f9); }
.ipf__caretBtn:focus-visible { outline: none; box-shadow: var(--g-focus-ring, 0 0 0 3px rgba(79,70,229,.16)); }
.ipf__caret { font-size: 11px; color: var(--g-text-disabled, #9aa2b1); transition: transform .16s ease; }
.ipf__combo.is-open .ipf__caret { transform: rotate(180deg); color: var(--g-blue, #4f46e5); }

/* ---- switch ---- */
.ipf__switch {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  height: 36px;
  /* Bootstrap is loaded globally and gives every <label> a .5rem bottom
     margin, which rides the switch off the centre line. */
  margin: 0;
  cursor: pointer;
  user-select: none;
  font-size: 13px;
  font-weight: 600;
  color: var(--g-text-secondary, #5d6675);
  white-space: nowrap;
}
.ipf__switch input { position: absolute; opacity: 0; width: 1px; height: 1px; }
.ipf__track {
  position: relative;
  width: 32px;
  height: 19px;
  border-radius: 999px;
  background: #d5d9e3;
  transition: background-color .18s ease;
  flex: 0 0 auto;
}
.ipf__thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px rgba(17, 24, 39, .28);
  transition: transform .18s ease;
}
.ipf__switch input:checked + .ipf__track { background: var(--g-blue, #4f46e5); }
.ipf__switch input:checked + .ipf__track .ipf__thumb { transform: translateX(13px); }
.ipf__switch input:focus-visible + .ipf__track { box-shadow: var(--g-focus-ring, 0 0 0 3px rgba(79,70,229,.16)); }
.ipf__switch input:checked ~ .ipf__switchText { color: var(--g-blue, #4f46e5); }

/* ---- the open list: in flow, so nothing is ever behind it ---- */
/* Short, because it holds the entries and nothing else — the add-field that
   used to sit in here is on the bar now, and the heading said what the field
   beside it already says. */
.ipf__panel {
  margin-top: 8px;
  padding: 5px;
  border: 1px solid var(--g-border, #e9ebf2);
  border-radius: var(--g-radius-sm, 9px);
  background: var(--g-surface-2, #fafbfd);
}
.ipf__count {
  min-width: 20px;
  padding: 1px 7px;
  border-radius: 999px;
  background: var(--g-blue-tint, #eef2ff);
  color: var(--g-blue, #4f46e5);
  font-size: 11.5px;
  letter-spacing: 0;
  text-align: center;
}


/* Capped at 5 rows of 36px and then it scrolls — twenty exclusions and three
   take the same room, which is the whole point of keeping this collapsed. */
.ipf__list {
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 180px;
  overflow-y: auto;
}
.ipf__row {
  display: flex;
  align-items: center;
  gap: 9px;
  min-height: 36px;
  padding: 4px 8px;
  border-radius: 7px;
  font-size: 14px;
  color: var(--g-text, #111827);
}
.ipf__row:hover { background: var(--g-hover, #f2f4f9); }
.ipf__row--warn { color: var(--g-amber, #b54708); }
.ipf__rowIcon { flex: 0 0 auto; font-size: 12px; opacity: .75; }
.ipf__rowText { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ipf__rowX {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: none;
  color: inherit;
  font-size: 12px;
  opacity: .55;
  cursor: pointer;
}
.ipf__rowX:hover { opacity: 1; background: rgba(79, 70, 229, .14); }
.ipf__row--warn .ipf__rowX:hover { background: rgba(181, 71, 8, .14); }
.ipf__empty {
  margin: 0;
  padding: 9px 8px;
  font-size: 13.5px;
  color: var(--g-text-secondary, #5b6475);
}

/* ---- account picker ---- */
.ipf__suggest {
  margin: 0 0 5px;
  padding: 3px;
  list-style: none;
  max-height: 180px;
  overflow-y: auto;
  border: 1px solid var(--g-border, #e9ebf2);
  border-radius: 7px;
  background: var(--g-surface, #fff);
}
.ipf__suggestItem {
  display: flex;
  align-items: center;
  gap: var(--g-sp-2, 8px);
  min-height: 36px;
  padding: 5px 9px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}
.ipf__suggestItem:hover,
.ipf__suggestItem.is-active { background: var(--g-blue-tint, #eef2ff); }
.ipf__suggestName { font-weight: 600; color: var(--g-text, #111827); white-space: nowrap; }
.ipf__suggestMeta {
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  color: var(--g-text-secondary, #5d6675);
}
.ipf__suggestEmpty {
  display: flex;
  align-items: center;
  gap: var(--g-sp-2, 8px);
  padding: 10px;
  font-size: 13.5px;
  color: var(--g-text-secondary, #5d6675);
}

/* ---- messages + the one Save ---- */
.ipf__msg {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 12.5px;
  line-height: 1.45;
}
.ipf__msg--warn { margin: 6px 4px 2px; color: var(--g-amber, #b54708); }
.ipf__msg--bad { color: var(--g-red, #b42318); }
/* The error sits on its own line under the bar. No footer rule any more — the
   Save button moved up onto the bar and took that row with it. */
.ipf__barMsg { margin-top: 8px; }
.ipf__saved {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: var(--g-green, #067647);
  white-space: nowrap;
}
.ipf__miniSpinner { width: 15px; height: 15px; border-width: 2px; }
.ipf-fade-enter-active, .ipf-fade-leave-active { transition: opacity .2s ease; }
.ipf-fade-enter-from, .ipf-fade-leave-to { opacity: 0; }
</style>
